/**
 * Timeline card registry.
 * Maps event types to card components for rendering.
 */

import { lazy, ComponentType } from 'react';
import type { InteractionEventResponse, InteractionMessageResponse } from '@vagusx/clients';

// Lazy load card components for code splitting
const VoiceCallCard = lazy(() => import('./cards/VoiceCallCard'));
const SmsSentCard = lazy(() => import('./cards/SmsSentCard'));
const AppointmentCard = lazy(() => import('./cards/AppointmentCard'));
const EhrSyncCard = lazy(() => import('./cards/EhrSyncCard'));
const TaskCard = lazy(() => import('./cards/TaskCard'));
const GenericCard = lazy(() => import('./cards/GenericCard'));

export interface TimelineCardProps {
  event: InteractionEventResponse;
  messages?: InteractionMessageResponse[];
  patientInitials?: string;
}

type CardComponent = ComponentType<TimelineCardProps>;

// Registry mapping event types to card components
const CARD_REGISTRY: Record<string, CardComponent> = {
  // Call events
  'call:started': VoiceCallCard,
  'call:completed': VoiceCallCard,
  'call:transferred': VoiceCallCard,

  // SMS events
  'sms:sent': SmsSentCard,
  'sms:received': SmsSentCard,
  'sms:delivered': SmsSentCard,
  'sms:failed': SmsSentCard,
  'sms:confirmation_sent': SmsSentCard,
  'sms:confirmation_failed': SmsSentCard,

  // Appointment events
  'appointment:booked': AppointmentCard,
  'appointment:cancelled': AppointmentCard,
  'appointment:rescheduled': AppointmentCard,

  // EHR events
  'ehr:synced': EhrSyncCard,
  'ehr:failed': EhrSyncCard,

  // Task events
  'task:created': TaskCard,
  'task:completed': TaskCard,
};

/**
 * Get the card component for an event type.
 * Returns GenericCard if no specific card is registered.
 */
export function getCardComponent(eventType: string): CardComponent {
  return CARD_REGISTRY[eventType] ?? GenericCard;
}

/**
 * Register a new card component for an event type.
 * Can be used for extensions or plugins.
 */
export function registerCard(eventType: string, component: CardComponent): void {
  CARD_REGISTRY[eventType] = component;
}

/**
 * Check if a specific card exists for an event type.
 */
export function hasSpecificCard(eventType: string): boolean {
  return eventType in CARD_REGISTRY;
}
